import { useState, useEffect } from "react";
import { Form, required, useDataProvider, useLogin, useNotify } from "ra-core";
import { useSetPassword, useSupabaseAccessToken } from "ra-supabase-core";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Lock, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Layout } from "@/components/supabase/layout";
import type { Sale } from "@/components/atomic-crm/types";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

interface SetPasswordFormData {
  email?: string;
  tempPassword?: string;
  password: string;
  confirmPassword: string;
}

export const SetPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  // Extrai parâmetros robustamente seja da query da URL, do hash router ou de searchParams
  const getParam = (key: string): string => {
    const fromRouter = searchParams.get(key);
    if (fromRouter) return fromRouter;

    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const fromSearch = urlParams.get(key);
      if (fromSearch) return fromSearch;

      const hash = window.location.hash;
      const qIndex = hash.indexOf("?");
      if (qIndex !== -1) {
        const hashParams = new URLSearchParams(hash.slice(qIndex));
        const fromHash = hashParams.get(key);
        if (fromHash) return fromHash;
      }
    }
    return "";
  };

  const emailParam = getParam("email");
  const tempParam = getParam("temp");

  const access_token = useSupabaseAccessToken();
  const refresh_token = useSupabaseAccessToken({
    parameterName: "refresh_token",
  });

  const notify = useNotify();
  const login = useLogin();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [, { mutateAsync: setPasswordViaSupabase }] = useSetPassword();

  // Se a URL contiver hash de erro (ex: #error=access_denied ao expirar link padrão do Supabase), limpa para não travar
  useEffect(() => {
    if (window.location.hash.includes("error=")) {
      console.warn(
        "Limpando hash de erro residual da URL:",
        window.location.hash,
      );
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  }, []);

  const validate = (values: SetPasswordFormData) => {
    const errors: Record<string, string> = {};
    if (!values.password || values.password.length < 6) {
      errors.password = "A senha deve ter no mínimo 6 caracteres.";
    }
    if (values.password !== values.confirmPassword) {
      errors.confirmPassword = "As senhas digitadas não coincidem.";
    }
    return errors;
  };

  const submit = async (values: SetPasswordFormData) => {
    setLoading(true);
    const targetEmail = (values.email || emailParam).trim();
    const tempPass = (values.tempPassword || tempParam || "").trim();

    try {
      // 1. Se tiver tokens na URL (fluxo nativo do Supabase Auth com recovery / invite OTP)
      if (access_token && refresh_token) {
        try {
          await setPasswordViaSupabase({
            access_token,
            refresh_token,
            password: values.password,
          });

          notify("Senha cadastrada com sucesso! Bem-vindo ao CRM DOTUS.", {
            type: "success",
          });
          navigate("/");
          return;
        } catch (tokenErr) {
          console.warn(
            "Falha ao usar tokens nativos, tentando método direto:",
            tokenErr,
          );
        }
      }

      // 2. Se tiver senha temporária (do convite Gmail ou preenchida)
      const client = getSupabaseClient();
      if (client) {
        if (tempPass) {
          // Loga com a senha provisória para criar uma sessão válida
          const { error: signInErr } = await client.auth.signInWithPassword({
            email: targetEmail,
            password: tempPass,
          });
          if (signInErr) {
            console.warn(
              "Tentativa com senha provisória falhou:",
              signInErr.message,
            );
          }
        }

        // Verifica se temos sessão autenticada ativa para atualizar a senha
        const {
          data: { session },
        } = await client.auth.getSession();

        if (session) {
          const { error: updateErr } = await client.auth.updateUser({
            password: values.password,
          });
          if (updateErr) {
            console.error("Erro ao atualizar senha no Supabase:", updateErr);
            throw updateErr;
          }
        }
      }

      // 3. Atualiza o registro do membro da equipe (sales) para marcar como ativo
      if (targetEmail) {
        try {
          const res = await dataProvider.getList<Sale>("sales", {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "id", order: "ASC" },
            filter: {},
          });

          const user = (res.data || []).find(
            (u) => u.email?.toLowerCase() === targetEmail.toLowerCase(),
          );

          if (user) {
            await dataProvider.update("sales", {
              id: user.id,
              data: {
                status: "active",
                disabled: false,
                account_configured: true,
                last_login: `Hoje às ${new Date().toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`,
              },
              previousData: user,
            });
          }
        } catch (dbErr) {
          console.warn("Erro ao atualizar status do usuário em sales:", dbErr);
        }
      }

      // 4. Realiza o login com a nova senha escolhida
      try {
        await login({ email: targetEmail, password: values.password }, "/");
      } catch {
        // Se o client já estiver autenticado pelo updateUser, vai direto
        const {
          data: { session: postSession },
        } = await (client?.auth.getSession() || { data: { session: null } });

        if (postSession) {
          notify("Senha salva com sucesso! Bem-vindo ao CRM DOTUS.", {
            type: "success",
          });
          navigate("/");
          return;
        }

        notify("Senha criada com sucesso! Faça login com sua nova senha.", {
          type: "success",
        });
        navigate("/login");
        return;
      }

      notify("Senha criada com sucesso! Bem-vindo ao CRM DOTUS.", {
        type: "success",
      });
      navigate("/");
    } catch (error: any) {
      console.error("Erro ao definir senha:", error);
      notify(
        error?.message ||
          "Não foi possível salvar a nova senha. Tente novamente.",
        { type: "warning" },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-1">
          <Lock className="h-6 w-6" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Defina sua senha de acesso
        </h1>
        <p className="text-sm text-muted-foreground">
          {emailParam
            ? `Bem-vindo! Crie uma senha definitiva para ${emailParam}.`
            : "Bem-vindo ao CRM DOTUS! Crie sua senha de acesso ao sistema."}
        </p>
      </div>

      <Form
        className="space-y-4"
        onSubmit={submit as any}
        validate={validate as any}
        defaultValues={{ email: emailParam, tempPassword: tempParam }}
      >
        {!access_token && (
          <TextInput
            label="Seu e-mail corporativo"
            source="email"
            type="email"
            validate={required()}
            defaultValue={emailParam}
            helperText={false}
          />
        )}

        {!tempParam && !access_token && (
          <TextInput
            label="Senha provisória (recebida no e-mail)"
            source="tempPassword"
            type="password"
            placeholder="Ex: Dotus@..."
            helperText="Caso tenha recebido uma senha provisória de ativação, insira-a aqui."
          />
        )}

        <TextInput
          label="Nova Senha (mínimo 6 caracteres)"
          autoComplete="new-password"
          source="password"
          type="password"
          validate={required()}
          helperText={false}
        />

        <TextInput
          label="Confirme a Nova Senha"
          autoComplete="new-password"
          source="confirmPassword"
          type="password"
          validate={required()}
          helperText={false}
        />

        <div className="pt-2">
          <Button
            type="submit"
            className="w-full flex items-center justify-center gap-2 cursor-pointer h-10 font-semibold"
            disabled={loading}
          >
            {loading ? "Salvando senha..." : "Salvar Senha e Entrar"}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="text-center pt-2">
          <Link
            to="/login"
            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            Já possui uma senha? Fazer login
          </Link>
        </div>
      </Form>
    </Layout>
  );
};

SetPasswordPage.path = "set-password";
